import React from 'react';
import './PrintableQuote.css'; // Import CSS for styling
import { format } from 'date-fns';

// Define the structure of the quote data prop
interface QuoteItem {
  structure: {
    code: string;
    type: string;
    location: string;
  };
  startDate: Date | string;
  endDate: Date | string;
  unitPrice: number;
  quantity: number;
  discountPercentage: number;
  finalPrice: number;
  isMonthlyRate: boolean | null;
}

interface ClientInfo {
  name: string;
  director?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

interface QuoteData {
  quoteNumber: string;
  quoteDate: Date | string;
  validUntil: Date | string;
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  termsAndConditions?: string | null;
  notes?: string | null;
  client: ClientInfo;
  items: QuoteItem[];
}

interface PrintableQuoteProps {
  quoteData: QuoteData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PrintableQuote = React.forwardRef<HTMLDivElement, PrintableQuoteProps>(({ quoteData }, ref) => {
  if (!quoteData) {
    return <div className="printable-container" ref={ref}>Ошибка: Данные для коммерческого предложения не найдены.</div>;
  }

  const {
    quoteNumber,
    quoteDate,
    validUntil,
    totalAmount,
    discountAmount,
    finalAmount,
    termsAndConditions,
    notes,
    client,
    items,
  } = quoteData;

  return (
    <div className="printable-container quote-container" ref={ref}>
      <header className="printable-header">
        <h1>Коммерческое предложение № {quoteNumber}</h1>
        <div className="header-details">
          <p>Дата: {formatDate(quoteDate)}</p>
          <p>Действительно до: {formatDate(validUntil)}</p>
        </div>
      </header>

      <section className="client-info">
        <h2>Информация о клиенте</h2>
        <p><strong>Компания:</strong> {client.name}</p>
        {client.director && <p><strong>Директор:</strong> {client.director}</p>}
        {client.phone && <p><strong>Телефон:</strong> {client.phone}</p>}
        {client.email && <p><strong>Email:</strong> {client.email}</p>}
        {client.address && <p><strong>Адрес:</strong> {client.address}</p>}
      </section>

      <section className="items-table">
        <h2>Позиции предложения</h2>
        <table>
          <thead>
            <tr>
              <th>№</th>
              <th>Конструкция (Код)</th>
              <th>Тип</th>
              <th>Локация</th>
              <th>Период</th>
              <th>Кол-во</th>
              <th>Цена за ед.</th>
              <th>Скидка (%)</th>
              <th>Итого</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const quantityLabel = item.isMonthlyRate === null ? 'ед.' : item.isMonthlyRate ? 'мес.' : 'дн.';
              const unitLabel = item.isMonthlyRate === null ? '/услуга' : item.isMonthlyRate ? '/мес' : '/день';

              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.structure.code}</td>
                  <td>{item.structure.type}</td>
                  <td>{item.structure.location}</td>
                  <td>{formatDate(item.startDate)} - {formatDate(item.endDate)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {item.quantity} {quantityLabel}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      {formatCurrency(item.unitPrice)} ({unitLabel})
                    </span>
                  </td>
                  <td>{item.discountPercentage.toFixed(1)}%</td>
                  <td>{formatCurrency(item.finalPrice)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="summary">
        <h2>Итоги</h2>
        <div className="summary-details">
          <p><strong>Общая сумма:</strong> {formatCurrency(totalAmount)}</p>
          <p><strong>Скидка:</strong> {formatCurrency(discountAmount)}</p>
          <p><strong>Итого к оплате:</strong> {formatCurrency(finalAmount)}</p>
        </div>
      </section>

      {notes && (
        <section className="notes">
          <h2>Примечания</h2>
          <p>{notes}</p>
        </section>
      )}

      {termsAndConditions && (
        <section className="terms">
          <h2>Условия и положения</h2>
          <p>{termsAndConditions}</p>
        </section>
      )}

      <footer className="printable-footer">
        <p>Спасибо за ваш интерес!</p>
        {/* You might add company details here */}
      </footer>

      {/* Optional: Add a print button for convenience */}
      {/* <button onClick={() => window.print()} className="print-button no-print">
        Распечатать
      </button> */}
    </div>
  );
});

export default PrintableQuote;
